import { useEffect, useState } from "react";
import {
  axiosInstance,
  buildLegacyHeaders,
  fetchDashboardNavigation,
} from "../Components/axiosFetchers.js";
import SupportIcon from "../Assets/Icons/SupportIcon.tsx";
import FarmIcon from "../Assets/Icons/FarmIcon.tsx";
import FieldIcon from "../Assets/Icons/FieldIcon.tsx";
import AddFarmFieldIcon from "../Assets/Icons/AddFarmFieldIcon.tsx";
import DashBoardIcon from "../Assets/Icons/DashBoardIcon.tsx";
import FieldLibraryIcon from "../Assets/Icons/FieldLibraryIcon.tsx";
import ProjectsIcon from "../Assets/Icons/ProjectsIcon.tsx";
import * as TableViewIconModule from "@mui/icons-material/TableView.js";
import {
  AGGREGATOR_SELECTED_GROWER_EVENT,
  DATA_READ_SCOPE,
  IMPORT_WRITE_SCOPE,
  RECONCILIATION_WRITE_SCOPE,
  readSelectedAggregatorGrower,
  selectedGrowerHasAnyScope,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";

function resolveMuiIcon(iconModule) {
  let candidate = iconModule;

  for (let index = 0; index < 4; index += 1) {
    if (typeof candidate === "function" || candidate?.$$typeof) {
      return candidate;
    }
    candidate = candidate?.default;
  }

  return iconModule;
}

const TableViewIcon = resolveMuiIcon(TableViewIconModule);

function unwrapList(payload) {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

function coerceBoolean(value, fallback = true) {
  if (value === undefined || value === null) {
    return fallback;
  }

  return Boolean(value);
}

function normalizeNavigationFarms(payload) {
  return (
    payload?.field_library?.farms ||
    payload?.grower_tree?.farms ||
    payload?.tree?.farms ||
    payload?.farms ||
    []
  );
}

function buildFarmTreeItems(farms = []) {
  return farms
    .map((farm) => {
      const farmId = farm?.id || farm?.farm_id;
      const farmName = farm?.name || farm?.farm_name;

      if (!farmId || !farmName) {
        return null;
      }

      const fields = Array.isArray(farm?.fields) ? farm.fields : [];

      return {
        icon: <FarmIcon />,
        title: farmName,
        to: `/farm/${farmId}`,
        items: [
          ...fields
            .map((field) => {
              const fieldId = field?.id || field?.field_id;
              const fieldName = field?.name || field?.field_name;

              if (!fieldId || !fieldName) {
                return null;
              }

              return {
                icon: <FieldIcon />,
                title: fieldName,
                to: `/farm/${farmId}/field/${fieldId}`,
              };
            })
            .filter(Boolean),
          {
            title: "Add Field",
            to: `/farm/${farmId}/field/form`,
            icon: <AddFarmFieldIcon />,
          },
        ],
      };
    })
    .filter(Boolean);
}

function baseMenu(items) {
  return items;
}

function staticPortalMenu(portal, selectedGrower = null) {
  if (portal === "partner") {
    return baseMenu([
      {
        icon: <DashBoardIcon />,
        title: "Dashboard",
        to: "/dashboard",
        borderColor: "#00ADEE",
      },
      {
        icon: <FieldLibraryIcon />,
        title: "Projects",
        to: "/projects",
        borderColor: "#34a853",
      },
      {
        icon: <SupportIcon />,
        title: "Profile",
        to: "/profile",
        borderColor: "#FFFFFF",
      },
    ]);
  }

  if (portal === "aggregator") {
    const canReadData = selectedGrowerHasScope(selectedGrower, DATA_READ_SCOPE);
    const canUseExcel = selectedGrowerHasAnyScope(selectedGrower, [
      DATA_READ_SCOPE,
      IMPORT_WRITE_SCOPE,
    ]);
    const canReconcile = selectedGrowerHasScope(
      selectedGrower,
      RECONCILIATION_WRITE_SCOPE
    );

    return baseMenu([
      ...(canReadData
        ? [
            {
              icon: <DashBoardIcon />,
              title: "Dashboard",
              to: "/dashboard",
              borderColor: "#00ADEE",
            },
          ]
        : []),
      {
        icon: <FarmIcon />,
        title: "Growers",
        to: "/growers",
        borderColor: "#34a853",
      },
      ...(canUseExcel
        ? [
            {
              icon: <TableViewIcon sx={{ color: "#969696", fontSize: 30 }} />,
              title: "Excel Exchange",
              to: "/excel",
              borderColor: "#d8a31a",
            },
          ]
        : []),
      ...(canReconcile
        ? [
            {
              icon: <SupportIcon />,
              title: "Reconciliation",
              to: "/reconciliation",
              borderColor: "#FFFFFF",
            },
          ]
        : []),
      {
        icon: <SupportIcon />,
        title: "Profile",
        to: "/profile",
        borderColor: "#FFFFFF",
      },
    ]);
  }

  if (portal === "admin") {
    return baseMenu([
      {
        icon: <DashBoardIcon />,
        title: "Dashboard",
        to: "/dashboard",
        borderColor: "#00ADEE",
      },
      {
        icon: <SupportIcon />,
        title: "User Impersonation",
        to: "/persona",
        borderColor: "#f15d22",
      },
      {
        icon: <AddFarmFieldIcon />,
        title: "Provision Users",
        to: "/workforce",
        borderColor: "#34a853",
      },
      {
        icon: <ProjectsIcon />,
        title: "Project Approvals",
        to: "/project-enrollments",
        borderColor: "#d8a31a",
      },
      {
        icon: <FieldLibraryIcon />,
        title: "Profile",
        to: "/profile",
        borderColor: "#FFFFFF",
      },
    ]);
  }

  return baseMenu([]);
}

export function useSideNavMenu(user, portal = "grower") {
  const [menu, setMenu] = useState([]);
  const [selectedGrower, setSelectedGrower] = useState(() =>
    readSelectedAggregatorGrower()
  );

  useEffect(() => {
    function syncSelectedGrower() {
      setSelectedGrower(readSelectedAggregatorGrower());
    }

    window.addEventListener(AGGREGATOR_SELECTED_GROWER_EVENT, syncSelectedGrower);
    window.addEventListener("storage", syncSelectedGrower);
    return () => {
      window.removeEventListener(
        AGGREGATOR_SELECTED_GROWER_EVENT,
        syncSelectedGrower
      );
      window.removeEventListener("storage", syncSelectedGrower);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadLegacyGrowerMenu() {
      const farmsResponse = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/farms`,
        {
          headers: buildLegacyHeaders(user),
        }
      );

      const farmItems = await Promise.all(
        unwrapList(farmsResponse.data).map(async (farm) => {
          if (!farm?.id) {
            return null;
          }

          const fieldsResponse = await axiosInstance.get(
            `${process.env.REACT_APP_API_URL}/farms/${farm.id}/fields`,
            {
              headers: buildLegacyHeaders(user),
            }
          );

          return {
            icon: <FarmIcon />,
            title: farm.name,
            to: `/farm/${farm.id}`,
            items: [
              ...unwrapList(fieldsResponse.data)
                .map((field) => {
                  if (!field?.id) {
                    return null;
                  }

                  return {
                    icon: <FieldIcon />,
                    title: field.name,
                    to: `/farm/${farm.id}/field/${field.id}`,
                  };
                })
                .filter(Boolean),
              {
                title: "Add Field",
                to: `/farm/${farm.id}/field/form`,
                icon: <AddFarmFieldIcon />,
              },
            ],
          };
        })
      );

      return baseMenu([
        {
          icon: <DashBoardIcon />,
          title: "Dashboard",
          items: [],
          to: "/dashboard",
          borderColor: "#00ADEE",
        },
        {
          icon: <FieldLibraryIcon />,
          title: "Field Library",
          items: [
            ...farmItems.filter(Boolean),
            {
              title: "Add Farm",
              to: "/farm/form",
              icon: <AddFarmFieldIcon />,
            },
          ],
          borderColor: "#34a853",
        },
        {
          icon: <ProjectsIcon />,
          title: "Projects",
          to: "/projects",
          borderColor: "#f15d22",
        },
        {
          icon: <FarmIcon />,
          title: "Equipment",
          to: "/equipment",
          borderColor: "#00ADEE",
        },
        {
          icon: <AddFarmFieldIcon />,
          title: "Fertilizers",
          to: "/fertilizers",
          borderColor: "#34a853",
        },
        {
          icon: <TableViewIcon sx={{ color: "#969696", fontSize: 30 }} />,
          title: "Excel Exchange",
          to: "/excel",
          borderColor: "#d8a31a",
        },
        {
          icon: <SupportIcon />,
          title: "Utilities",
          to: "/utilities",
          borderColor: "#b026ff",
        },
      ]);
    }

    async function loadMenu() {
      if (portal !== "grower") {
        if (isMounted) {
          setMenu(staticPortalMenu(portal, selectedGrower));
        }
        return;
      }

      if (!user?.token) {
        if (isMounted) {
          setMenu(baseMenu([]));
        }
        return;
      }

      try {
        const navigation = await fetchDashboardNavigation(user);
        const farms = normalizeNavigationFarms(navigation);
        const farmItems = buildFarmTreeItems(farms);
        const canViewProjects = coerceBoolean(
          navigation?.permissions?.can_view_projects ?? navigation?.can_view_projects,
          true
        );
        const canViewUtilities = coerceBoolean(
          navigation?.permissions?.can_view_utilities ?? navigation?.can_view_utilities,
          true
        );

        if (farmItems.length > 0) {
          if (!isMounted) {
            return;
          }

          setMenu(
            baseMenu([
              {
                icon: <DashBoardIcon />,
                title: "Dashboard",
                items: [],
                to: "/dashboard",
                borderColor: "#00ADEE",
              },
              {
                icon: <FieldLibraryIcon />,
                title: "Field Library",
                items: [
                  ...farmItems,
                  {
                    title: "Add Farm",
                    to: "/farm/form",
                    icon: <AddFarmFieldIcon />,
                  },
                ],
                borderColor: "#34a853",
              },
              ...(canViewProjects
                ? [
                    {
                      icon: <ProjectsIcon />,
                      title: "Projects",
                      to: "/projects",
                      borderColor: "#f15d22",
                    },
                  ]
                : []),
              {
                icon: <FarmIcon />,
                title: "Equipment",
                to: "/equipment",
                borderColor: "#00ADEE",
              },
              {
                icon: <AddFarmFieldIcon />,
                title: "Fertilizers",
                to: "/fertilizers",
                borderColor: "#34a853",
              },
              {
                icon: <TableViewIcon sx={{ color: "#969696", fontSize: 30 }} />,
                title: "Excel Exchange",
                to: "/excel",
                borderColor: "#d8a31a",
              },
              ...(canViewUtilities
                ? [
                    {
                      icon: <SupportIcon />,
                      title: "Utilities",
                      to: "/utilities",
                      borderColor: "#b026ff",
                    },
                  ]
                : []),
            ])
          );
          return;
        }
      } catch (error) {
        console.error("Failed to load dashboard navigation summary", error);
      }

      if (!isMounted) {
        return;
      }

      setMenu(await loadLegacyGrowerMenu());
    }

    loadMenu().catch((error) => {
      console.error("Failed to load side navigation", error);
      if (isMounted) {
        setMenu(staticPortalMenu(portal, selectedGrower));
      }
    });

    return () => {
      isMounted = false;
    };
  }, [portal, selectedGrower, user]);

  return {
    menu: [...menu, { icon: <SupportIcon />, title: "Support", to: "/support", borderColor: "#b026ff" }],
  };
}
